import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  InputGroup,
  InputRightElement,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import CompanyResource from 'api/company';
import UserResource from 'api/user';
import {
  activeStyles,
  normalStyles,
  reactSelectStyles,
  separatorStyles,
} from 'assets/css/commonStyles';
import {
  Container,
  Next,
  PageGroup,
  Paginator,
  Previous,
  usePaginator,
} from 'chakra-paginator';
import PageLimitChanger from 'components/common/PageLimitChanger';
import ResultCountDisplay from 'components/common/ResultCountDisplay';
import TableSkeletonLoader from 'components/common/TableSkeletonLoader';
import UserListItemReport from 'components/reports/UserListItemReport';
import CreatedAtInput, {
  DEFAULT_PAGE_SIZE,
  INITIAL_CURRENT_PAGE,
  REACT_DATE_PICKER_FORMAT_YYYY_MM_DD,
} from 'constants/common';
import { VendorSchema } from 'constants/schema';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, useForm } from 'react-hook-form';
import {
  BiCalendar,
  BiChevronLeft,
  BiChevronRight,
  BiExport,
} from 'react-icons/bi';
import { useQuery } from 'react-query';
import ReactSelect from 'react-select';
import { DateFormatMDY, getStartingSerialNumber } from 'utils';
interface FilterParams {
  currentPage: number;
  pageSize: number;
  createdAt: string;
  keyword: string;
}

const UserListReport: React.FC = () => {
  const [isLoading, setIsLoading] = useState<string | null>();
  const [refetch, setRefetch] = useState<number>(1);
  const userAPI = new UserResource();
  const [createdAt, setCreatedAt] = useState<any>();

  const companyAPI = new CompanyResource();

  const companyQuery = useQuery(
    [`company-list`],
    () =>
      companyAPI
        .itemList({ is_active: true, is_approved: true, limit: 50 })
        .then((res) => res?.data?.data),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );
  const companyList = companyQuery?.data?.results;
  const companyOptions = companyList?.map((company: any) => ({
    label: company.name,
    value: company.id,
  }));

  const [totalUsers, setTotalUsers] = useState<number | undefined>(undefined);
  const [filterParams, setFilterParams] = useState<FilterParams>({
    currentPage: INITIAL_CURRENT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    createdAt: '',
    keyword: '',
  });

  const { pagesQuantity, setPageSize } = usePaginator({
    total: totalUsers,
    initialState: {
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: INITIAL_CURRENT_PAGE,
    },
  });

  useEffect(() => {
    if (createdAt) {
      setFilterParams((prevState) => ({
        ...prevState,
        createdAt: DateFormatMDY(createdAt),
      }));
    }
  }, [createdAt]);

  const { data: userList, isLoading: isUserListLoading } = useQuery(
    [
      'userList',
      {
        page: filterParams.currentPage,
        limit: filterParams.pageSize,
        createdAt: filterParams.createdAt,
        keyword: filterParams.keyword,
      },
    ],
    async () => {
      const queryParams: any = {
        page: filterParams.currentPage,
        limit: filterParams.pageSize,
      };
      if (filterParams.createdAt) queryParams.role_id = filterParams.createdAt;
      if (filterParams.keyword) queryParams.keyword = filterParams.keyword;
      const response = await userAPI.list(queryParams);
      setTotalUsers(response?.data?.data?.meta?.total);
      response && setIsLoading(null);
      return response?.data?.data?.results;
    }
  );

  const methods = useForm<any>();

  const filterList = async (data: any) => {
    setIsLoading(() => 'filter');
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: INITIAL_CURRENT_PAGE,
      createdAt: data.role_id,
      keyword: data.name,
    }));
  };

  const handlePageChange = (nextPage: number) => {
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: nextPage,
    }));
  };

  /**
   * Handle reset filter params
   */
  const handleReset = () => {
    setIsLoading(() => 'reset');
    methods.reset();
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: INITIAL_CURRENT_PAGE,
      createdAt: '',
      keyword: '',
    }));
  };

  const startingSN = useMemo(() => {
    return getStartingSerialNumber(
      filterParams.currentPage,
      filterParams.pageSize
    );
  }, [filterParams.currentPage, filterParams.pageSize]);

  /**
   * Handle filter params submit
   */
  const onSubmit = (data: any) => {
    filterList(data);
  };

  return (
    <>
      <Stack direction="column" spacing="4">
        <Flex justify="space-between">
          <Heading size="md">User Report</Heading>
          <IconButton
            size="sm"
            fontSize="md"
            // onClick={handleExport}
            variant="outline"
            rounded="sm"
            colorScheme="purple"
            aria-label="Export"
            icon={<BiExport />}
          />
        </Flex>

        <Stack
          direction="column"
          spacing="4"
          bg="white"
          p={['3', '6']}
          shadow="box"
          w="full">
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack
              direction={['column', 'row']}
              spacing="4"
              align={['start', 'end']}>
              <FormControl>
                <FormLabel>Date</FormLabel>
                <InputGroup>
                  <InputRightElement
                    children={<BiCalendar color="green.500" />}
                  />
                  <ReactDatePicker
                    name="created_at"
                    value={filterParams.createdAt}
                    placeholderText={'Select Date'}
                    dateFormat={REACT_DATE_PICKER_FORMAT_YYYY_MM_DD}
                    selected={createdAt}
                    customInput={<CreatedAtInput value={createdAt} />}
                    onChange={(date: Date) => setCreatedAt(date)}
                  />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel>Company</FormLabel>
                <Controller
                  control={methods.control}
                  name="company"
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      id="company"
                      placeholder={'Select Company'}
                      options={companyOptions}
                      styles={reactSelectStyles}
                      isSearchable
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>User</FormLabel>
                <Select
                  placeholder="Select User"
                  rounded="sm"
                  {...methods.register('user_id')}>
                  <option>User 1</option>
                  <option>User 2</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Location</FormLabel>
                <Select
                  placeholder="Select Location"
                  rounded="sm"
                  {...methods.register('location_id')}>
                  <option>Location 1</option>
                  <option>Location 2</option>
                </Select>
              </FormControl>
              <Stack direction="row" spacing="4" pt={['2', '8']}>
                <Button
                  isLoading={isLoading === 'filter'}
                  colorScheme="primary"
                  type="submit">
                  Filter
                </Button>
                <Button
                  colorScheme="primary"
                  isLoading={isLoading === 'reset'}
                  type="submit"
                  variant="outline"
                  onClick={handleReset}>
                  Reset Filter
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>

        <Stack bg="white" p={['3', '6']} shadow="box" rounded="sm">
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Company Name</Th>
                  <Th>Last Name</Th>
                  <Th>First Name</Th>
                  <Th>Date Added</Th>
                  <Th>Date Removed</Th>
                  <Th isNumeric>Number of Jobs Worked</Th>
                  <Th isNumeric>Total Hours Worked</Th>
                </Tr>
              </Thead>
              <Tbody>
                {!isUserListLoading &&
                  userList?.map((userData: VendorSchema, index: number) => (
                    <UserListItemReport
                      userData={userData}
                      key={userData.id}
                      index={startingSN + index}
                    />
                  ))}
                {isUserListLoading && (
                  <TableSkeletonLoader rows={filterParams.pageSize} cols={8} />
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          overflow="auto">
          <Text color="gray.400">
            <ResultCountDisplay
              isLoading={isUserListLoading}
              totalCount={totalUsers}
              resultCount={userList?.length}
              pageSize={filterParams.pageSize}
              currentPage={filterParams.currentPage}
            />
          </Text>
          <Flex alignItems="center" flexWrap={['wrap', 'nowrap']}>
            {userList?.length > 0 && (
              <PageLimitChanger
                pageSize={filterParams.pageSize}
                setPageSize={(value: any) => {
                  setFilterParams((prevState) => ({
                    ...prevState,
                    currentPage: INITIAL_CURRENT_PAGE,
                    pageSize: value,
                  }));
                  setPageSize(value);
                }}
              />
            )}
            {!isUserListLoading && pagesQuantity > 1 && (
              <Paginator
                activeStyles={activeStyles}
                currentPage={filterParams.currentPage}
                normalStyles={normalStyles}
                separatorStyles={separatorStyles}
                pagesQuantity={pagesQuantity}
                onPageChange={handlePageChange}
                innerLimit={2}
                outerLimit={2}>
                <Container
                  align="center"
                  justifyContent="flex-end"
                  w="full"
                  p={1}
                  pr={0}
                  ml={3}>
                  <Previous mr="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronLeft} fontSize="xl" />
                  </Previous>
                  <PageGroup isInline align="center" />
                  <Next ml="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronRight} fontSize="xl" />
                  </Next>
                </Container>
              </Paginator>
            )}
          </Flex>
        </Flex>
      </Stack>
    </>
  );
};

export default UserListReport;
